import {
  AfterContentInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Product, ProductComposition, ProductModule } from 'src/app/models';
import { PickerType } from 'src/app/models/product-detail';
import { buttonIcons } from 'src/app/modules/icon.module';
import { ContentService } from 'src/app/services/content/content.service';
import { KioskService } from 'src/app/services/kiosk/kiosk.service';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { IconButtonComponent, TextButtonComponent } from 'src/app/shared/components';
import { ProductCardComponent } from 'src/app/shared/components/product/card';
import { CounterComponent } from 'src/app/shared/components/product/counter/counter.component';
import { ContentSection } from 'src/app/shared/layout/content-section';
import { PrimaryProductImagePipe } from '../../../../../../shared/pipes/primary-product-image.pipe';
import { ProductImageSetPipe } from '../../../../../../shared/pipes/product-image-set.pipe';

@Component({
  selector: 'app-product-picker',
  templateUrl: './product-picker.component.html',
  styleUrls: ['./product-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatIconModule,
    ProductImageSetPipe,
    PrimaryProductImagePipe,
    TextButtonComponent,
    IconButtonComponent,
    CounterComponent,
    ProductCardComponent,
  ],
})
export class ProductPickerComponent extends ContentSection implements OnInit, AfterContentInit {
  private themeService = inject(ThemeService);
  private elRef = inject(ElementRef);
  private kioskService = inject(KioskService);

  clearSelectionIcon = buttonIcons.bigCross;

  @HostBinding('class.kiosk') isKiosk: boolean;

  @Input() module!: ProductModule;
  @Input() composition: ProductComposition;
  @Input() locked: boolean;
  @Input() highlightRequired: boolean;
  @Input() pictureCards: boolean;

  @Output() add = new EventEmitter<[ProductModule, Product, PickerType, number]>();
  @Output() remove = new EventEmitter<[ProductModule, Product, PickerType, number]>();

  type: PickerType;

  title: string;
  selectLabel: string;
  selectedLabel: string;
  pickCountLabel: string;
  pickCountWarningLabel: string;
  hideImages: boolean;

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, 'productInfo');

    this.selectLabel = this.content.get('selectLabelText');
    this.selectedLabel = this.content.get('selectedLabelText');

    this.isKiosk = this.kioskService.isKiosk;
  }

  ngOnInit() {
    const { minPickCount, maxPickCount, singles, name } = this.module;
    this.title = name;
    this.hideImages = !this.containsImages(this.module.products);

    if (minPickCount || maxPickCount) {
      if (!minPickCount && maxPickCount) {
        this.pickCountLabel = `Vælg op til ${maxPickCount} stk.`;
        this.pickCountWarningLabel = `"Du kan højst vælge ${maxPickCount} stk."`;
      } else if (minPickCount && !maxPickCount) {
        this.pickCountLabel = `Vælg minimum ${minPickCount} stk.`;
        this.pickCountWarningLabel = `Du skal minimum vælge ${minPickCount} stk.`;
      } else if (minPickCount === maxPickCount) {
        this.pickCountLabel = `Vælg ${minPickCount} stk.`;
        this.pickCountWarningLabel = `Du skal vælge ${minPickCount} stk.`;
      } else if (minPickCount && maxPickCount && minPickCount != maxPickCount) {
        this.pickCountLabel = `Vælg mellem ${minPickCount} og ${maxPickCount} stk.`;
        this.pickCountWarningLabel = `Du skal vælge mellem ${minPickCount} og ${maxPickCount} stk.`;
      }
    }

    if (maxPickCount === 1) {
      this.type = 'pick-one';
      return;
    }

    if (singles) {
      this.type = 'pick-many';
    }

    if (!singles) {
      this.type = 'add';
    }
  }

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  addProduct(product: Product, quantity: number) {
    if (this.type === 'pick-one' || this.type === 'pick-many') quantity = 1;
    this.add.emit([this.module, product, this.type, quantity]);
  }

  removeProduct(product: Product, quantity: number) {
    this.remove.emit([this.module, product, this.type, quantity]);
  }

  private containsImages(products: Product[]): boolean {
    for (const product of products) {
      if (product.primaryImageUrl) return true;
    }

    return false;
  }
}
